/**
 * Role-Based Access Control and Data Isolation Utilities
 *
 * Enforces privacy and isolation rules:
 * - Normal users only see their own published projects (and projects they are members of).
 * - Normal users only see people related to their published projects (team members or matching skills).
 * - Private details (phone number, raw email) are masked/hidden for normal users.
 * - Admins retain full visibility across all projects and people.
 */

export const isProjectOwnerOrMember = (project, currentUser) => {
  if (!currentUser) return false;
  if (currentUser.role === 'admin') return true;

  const userEmail = (currentUser.email || '').toLowerCase().trim();
  const userName = (currentUser.name || '').toLowerCase().trim();

  // Match creatorEmail
  if (project.creatorEmail && project.creatorEmail.toLowerCase().trim() === userEmail) {
    return true;
  }
  // Match creator name
  if (project.creator && project.creator.toLowerCase().trim() === userName) {
    return true;
  }
  // Check teamMembers list
  if (Array.isArray(project.teamMembers)) {
    const isMember = project.teamMembers.some(member => {
      if (typeof member === 'string') {
        const m = member.toLowerCase().trim();
        return m === userName || m === userEmail;
      }
      if (member && typeof member === 'object') {
        return (member.email && member.email.toLowerCase().trim() === userEmail) ||
               (member.name && member.name.toLowerCase().trim() === userName);
      }
      return false;
    });
    if (isMember) return true;
  }

  return false;
};

/**
 * Returns projects the user owns or is already a member of.
 */
export const getUserOwnedOrMemberProjects = (projects, currentUser, isAdmin) => {
  if (isAdmin || currentUser?.role === 'admin') {
    return projects || [];
  }
  return (projects || []).filter(p => isProjectOwnerOrMember(p, currentUser));
};

/**
 * Returns all projects viewable by the user.
 * In SkillMesh, normal users are empowered to explore all projects, inspect required skills, and apply.
 */
export const getUserAccessibleProjects = (projects, currentUser, isAdmin) => {
  return projects || [];
};

/**
 * Calculates skill fit / match between project requirements and a user's skills
 */
export const calculateProjectSkillMatch = (projectSkills = [], userSkills = []) => {
  const normProjectSkills = (projectSkills || []).map(s => {
    return typeof s === 'string' ? s.trim() : (s?.name || '').trim();
  }).filter(Boolean);

  const normUserSkillNames = new Set(
    (userSkills || []).map(s => {
      return (typeof s === 'string' ? s : s?.name || '').toLowerCase().trim();
    }).filter(Boolean)
  );

  if (normProjectSkills.length === 0) {
    return {
      matchCount: 0,
      totalRequired: 0,
      matchPercentage: 0,
      matchedSkills: [],
      missingSkills: [],
      isGoodFit: false
    };
  }

  const matchedSkills = [];
  const missingSkills = [];

  normProjectSkills.forEach(skillName => {
    if (normUserSkillNames.has(skillName.toLowerCase())) {
      matchedSkills.push(skillName);
    } else {
      missingSkills.push(skillName);
    }
  });

  const matchCount = matchedSkills.length;
  const totalRequired = normProjectSkills.length;
  const matchPercentage = Math.round((matchCount / totalRequired) * 100);

  return {
    matchCount,
    totalRequired,
    matchPercentage,
    matchedSkills,
    missingSkills,
    isGoodFit: matchPercentage >= 50
  };
};

export const getRelatedPeopleForUser = (allPeople, userProjects, currentUser, isAdmin) => {
  if (isAdmin || currentUser?.role === 'admin') {
    return allPeople || [];
  }
  if (!userProjects || userProjects.length === 0) {
    return [];
  }

  const requiredSkills = new Set();
  const teamMemberIdentifiers = new Set();

  userProjects.forEach(project => {
    // Collect required skills from projects
    if (Array.isArray(project.requiredSkills)) {
      project.requiredSkills.forEach(s => {
        const skillName = typeof s === 'string' ? s : s?.name;
        if (skillName) requiredSkills.add(skillName.toLowerCase().trim());
      });
    }
    if (Array.isArray(project.requiredSkillsDetails)) {
      project.requiredSkillsDetails.forEach(s => {
        if (s?.name) requiredSkills.add(s.name.toLowerCase().trim());
      });
    }

    // Collect team members
    if (Array.isArray(project.teamMembers)) {
      project.teamMembers.forEach(m => {
        const id = typeof m === 'string' ? m : (m?.email || m?.name);
        if (id) teamMemberIdentifiers.add(id.toLowerCase().trim());
      });
    }
  });

  return (allPeople || []).filter(person => {
    const personName = (person.name || '').toLowerCase().trim();
    const personEmail = (person.email || '').toLowerCase().trim();

    // Already part of the team
    if (teamMemberIdentifiers.has(personName) || teamMemberIdentifiers.has(personEmail)) {
      return true;
    }

    // Has at least one skill matching the user's projects
    if (Array.isArray(person.skills)) {
      const matchesSkill = person.skills.some(skill => {
        const sName = typeof skill === 'string' ? skill : skill?.name;
        return sName && requiredSkills.has(sName.toLowerCase().trim());
      });
      if (matchesSkill) return true;
    }

    return false;
  });
};

export const getPersonProjectMatchDetails = (person, userProjects) => {
  if (!person || !userProjects || userProjects.length === 0) {
    return { matchingProjects: [], matchedSkills: [] };
  }

  const matchingProjects = [];
  const matchedSkillsSet = new Set();
  const personSkills = (person.skills || []).map(s => (typeof s === 'string' ? s : s?.name || '').toLowerCase());

  userProjects.forEach(project => {
    const projectSkills = (project.requiredSkills || []).map(s => (typeof s === 'string' ? s : s?.name || '').toLowerCase());
    const overlaps = personSkills.filter(s => s && projectSkills.includes(s));
    if (overlaps.length > 0) {
      matchingProjects.push({
        id: project.id,
        name: project.name,
        overlappingCount: overlaps.length
      });
      overlaps.forEach(s => matchedSkillsSet.add(s));
    }
  });

  return {
    matchingProjects,
    matchedSkills: Array.from(matchedSkillsSet)
  };
};

export const maskEmail = (email) => {
  if (!email || !email.includes('@')) return '••••••••';
  const [user, domain] = email.split('@');
  if (user.length <= 2) return `${user[0]}***@${domain}`;
  return `${user.slice(0, 2)}***@${domain}`;
};
